import { useState, useCallback } from "react";
import axios from "../customAxios.js";

/**
 * @typedef {object} WarehouseData
 * @property {number | null} id
 * @property {string | null} name
 * @property {string | null} customer_phone
 * @property {string | null} storekeeper_email
 * @property {string | null} storekeeper_name
 * @property {string | null} storekeeper_phone
 * @property {string | null} warehouse_address_comments
 * @property {number | null} capacity
 * @property {object | null} company
 * @property {object | null} address
 * @property {boolean} go_ex_delivery
 */

/**
 * Custom Hook to manage company details page and related actions
 *
 * It is responsible for obtaining warehouse data when mounting, and provides
 * functions to manipulate data
 * Automatically controls loading and error states
 *
 * @returns {{
 * warehouses: Array<WarehouseData> | null,
 * loading: boolean,
 * error: string | null,
 * fetchCompanyWarehouses: (companyId: number) => Promise<void>,
 * refetchWarehouses: () => Promise<void>
 * }} An object with user status and functions to interact with the API
 *
 * @example
 * const { warehouses, loading, error } = useWarehouses();
 */

export function useWarehouses() {
  // Data status and IU
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // STATE MANNERS (HANDLERS)
  const fetchAllWarehouses = useCallback(async() => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: { warehouses },
      } = await axios.get(`warehouses/`);
      setWarehouses(warehouses);
    } catch (error) {
      console.error(`No se pudo obtener las bodegas de la compañia: ${error}`);
      setError("No se pudo obtener las bodegas de la compañia");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompanyWarehouses = useCallback(async (companyId) => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: { serialized_warehouses: warehouses },
      } = await axios.get(`warehouses/get_company_warehouses/${companyId}`);
      setWarehouses(warehouses);
    } catch (error) {
      console.error(`No se pudo obtener las bodegas de la compañia: ${error}`);
      setError("No se pudo obtener las bodegas de la compañia");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettingEcommerceWarehouses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get("warehouses/all_warehouses/");
      setWarehouses(data);
    } catch (error) {
      console.error(`No se pudo obtener las bodegas de la compañia: ${error}`);
      setError("No se pudo obtener las bodegas de la compañia");
    } finally {
      setLoading(false);
    }
  }, []);

  const updateWarehousePickupSchedule = useCallback(async (warehouseId, scheduleData) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.patch(
        `warehouses/update_pickup_schedule/${warehouseId}/`,
        scheduleData,
      );
      setWarehouses((prev) => prev.map((w) => (w.id === warehouseId ? data : w)));
      return data;
    } catch (error) {
      console.error(`Error al actualizar horario de retiro: ${error}`);
      setError("No se pudo actualizar el horario de retiro");
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    warehouses,
    loading,
    error,

    // MANNERS (Setters y Actions)
    fetchCompanyWarehouses,
    fetchAllWarehouses,
    fetchSettingEcommerceWarehouses,
    updateWarehousePickupSchedule,
  };
}
